import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  collection, 
  query, 
  onSnapshot,
  updateDoc,
  doc,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { motion } from 'framer-motion';

const AdminDashboard = () => {
  const { currentUser, userData } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    approvedBookings: 0,
    totalUsers: 0,
    newSubmissions: 0
  });

  useEffect(() => {
    if (!currentUser || userData?.userType !== 'admin') {
      setBookings([]);
      setUsers([]);
      setContactSubmissions([]);
      setLoading(false);
      return;
    }

    // Fetch all bookings
    const bookingsQuery = query(collection(db, 'bookings'));
    const unsubscribeBookings = onSnapshot(bookingsQuery, 
      (snapshot) => {
        const bookingsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBookings(bookingsData);
      },
      (error) => {
        console.error('Error fetching bookings:', error);
        if (error.code === 'permission-denied') {
          console.log('Permission denied for bookings');
        }
      }
    );

    return () => unsubscribeBookings();
  }, [currentUser, userData]);

  useEffect(() => {
    if (!currentUser || userData?.userType !== 'admin') return;

    // Fetch all users
    const fetchUsers = async () => {
      try {
        const q = query(collection(db, 'users'));
        const querySnapshot = await getDocs(q);
        const usersData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        if (error.code === 'permission-denied') {
          console.log('Permission denied for users');
        }
      }
    };

    fetchUsers();
  }, [currentUser, userData]);

  // Fetch contact submissions with real-time updates
  useEffect(() => {
    if (!currentUser || userData?.userType !== 'admin') return;

    const contactQuery = query(collection(db, 'contactSubmissions'));
    const unsubscribeContact = onSnapshot(contactQuery, 
      (snapshot) => {
        const submissionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort by creation date, newest first
        const sortedSubmissions = submissionsData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
        setContactSubmissions(sortedSubmissions);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching contact submissions:', error);
        if (error.code === 'permission-denied') {
          console.log('Permission denied for contact submissions');
        }
        setLoading(false);
      }
    );

    return () => unsubscribeContact();
  }, [currentUser, userData]);

  // Update stats when data changes
  useEffect(() => {
    setStats({
      totalBookings: bookings.length,
      pendingBookings: bookings.filter(b => b.status === 'pending').length,
      approvedBookings: bookings.filter(b => b.status === 'approved').length,
      totalUsers: users.length,
      newSubmissions: contactSubmissions.filter(sub => !sub.read).length
    });
  }, [bookings, users, contactSubmissions]);

  const handleApproveBooking = async (bookingId) => {
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'approved',
        approvedAt: new Date(),
        'notifications.user': 'Your booking has been approved! Drivers will contact you soon.',
        'notifications.drivers': 'New approved booking available'
      });
    } catch (error) {
      console.error('Error approving booking:', error);
      alert('Error approving booking. Please try again.');
    }
  };

  const handleRejectBooking = async (bookingId) => {
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'rejected',
        rejectedAt: new Date(),
        'notifications.user': 'Your booking has been rejected. Please contact support for more information.'
      });
    } catch (error) {
      console.error('Error rejecting booking:', error);
      alert('Error rejecting booking. Please try again.');
    }
  };

  const markAsRead = async (submissionId) => {
    try {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      console.log('Marking submission as read:', submissionId);
      
      await updateDoc(doc(db, 'contactSubmissions', submissionId), {
        read: true,
        readBy: currentUser.uid,
        readAt: new Date()
      });

      console.log('Successfully marked as read');
      
    } catch (error) {
      console.error('Error marking as read:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        submissionId: submissionId,
        currentUser: currentUser?.uid
      });
      
      if (error.code === 'permission-denied') {
        alert('Permission denied. Please check Firestore rules.');
      } else if (error.code === 'not-found') {
        alert('Submission not found. It may have been deleted.');
      } else {
        alert('Error marking submission as read. Please try again.');
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadSubmissions = contactSubmissions.filter(sub => !sub.read);
      
      for (const submission of unreadSubmissions) {
        await updateDoc(doc(db, 'contactSubmissions', submission.id), {
          read: true,
          readBy: currentUser.uid,
          readAt: new Date()
        });
      }
      
      alert(`Marked ${unreadSubmissions.length} messages as read`);
    } catch (error) {
      console.error('Error marking all as read:', error);
      alert('Error marking messages as read. Please try again.');
    }
  };

  const statCards = [
    {
      title: 'Total Bookings',
      value: stats.totalBookings,
      color: 'blue',
      icon: '📊'
    },
    {
      title: 'Pending Approval',
      value: stats.pendingBookings,
      color: 'yellow',
      icon: '⏳'
    },
    {
      title: 'Approved Bookings',
      value: stats.approvedBookings,
      color: 'green',
      icon: '✅'
    },
    {
      title: 'Total Users',
      value: stats.totalUsers,
      color: 'purple',
      icon: '👥'
    },
    {
      title: 'New Messages',
      value: stats.newSubmissions,
      color: 'red',
      icon: '📩'
    }
  ];

  // Show access denied if not admin
  if (!currentUser || userData?.userType !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500">You need to be logged in as admin to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">
            Admin Dashboard
          </h1>
          <p className="text-gray-600">Welcome back, {userData?.firstName}!</p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <div className="flex items-center">
                <div className="text-3xl mr-4">{stat.icon}</div>
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'bookings', name: 'Bookings', count: bookings.length },
                { id: 'users', name: 'Users', count: users.length },
                { id: 'messages', name: 'Contact Messages', count: contactSubmissions.length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name} ({tab.count})
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'bookings' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold mb-4">Manage Bookings</h3>
                {bookings.map((booking, index) => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-4 bg-gray-50"
                  >
                    {/* ... booking content remains the same ... */}
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold">{booking.carType} - {booking.city}</h4>
                        <p className="text-sm text-gray-600">
                          {booking.pickupLocation} → {booking.destination}
                        </p>
                        <p className="text-sm text-gray-600">
                          {booking.date?.toDate?.() ? new Date(booking.date.toDate()).toLocaleString() : 'Date not set'}
                        </p>
                        <p className="text-sm font-semibold">£{booking.price?.total}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        booking.status === 'approved' ? 'bg-green-100 text-green-800' :
                        booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-600">
                        <p>User: {booking.userEmail}</p>
                        <p>Phone: {booking.userPhone || 'Not provided'}</p>
                        <p>Hours: {booking.hours}</p>
                      </div>
                      
                      {booking.status === 'pending' && (
                        <div className="space-x-2">
                          <button
                            onClick={() => handleApproveBooking(booking.id)}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectBooking(booking.id)}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {booking.notifications?.admin && (
                      <p className="text-xs text-blue-600 mt-2 bg-blue-50 p-2 rounded">
                        {booking.notifications.admin}
                      </p>
                    )}
                  </motion.div>
                ))}
                
                {bookings.length === 0 && (
                  <p className="text-gray-500 text-center py-8">
                    No bookings found
                  </p>
                )}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold mb-4">Registered Users</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {users.map((user, index) => (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="border rounded-lg p-4 bg-white"
                    >
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.firstName?.[0]}{user.lastName?.[0]}
                        </div>
                        <div className="ml-3">
                          <h4 className="font-semibold">
                            {user.firstName} {user.lastName}
                          </h4>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                      </div>
                      
                      <div className="text-sm space-y-1">
                        <p>
                          <span className="font-medium">Type:</span>{' '}
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.userType === 'admin' ? 'bg-purple-100 text-purple-800' :
                            user.userType === 'driver' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {user.userType}
                          </span>
                        </p>
                        <p><span className="font-medium">City:</span> {user.city}</p>
                        <p><span className="font-medium">Phone:</span> {user.phone}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {users.length === 0 && (
                  <p className="text-gray-500 text-center py-8">
                    No users found
                  </p>
                )}
              </div>
            )}

            {activeTab === 'messages' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Contact Submissions</h3>
                  {stats.newSubmissions > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      Mark All as Read ({stats.newSubmissions})
                    </button>
                  )}
                </div>
                
                {contactSubmissions.map((submission, index) => (
                  <motion.div
                    key={submission.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`border rounded-lg p-6 ${
                      submission.read ? 'bg-gray-50' : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-semibold">
                          {submission.firstName} {submission.lastName}
                        </h4>
                        <p className="text-gray-600">{submission.email}</p>
                        <p className="text-sm font-medium mt-1 text-blue-600">
                          {submission.subject}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          submission.read ? 'bg-gray-200 text-gray-700' : 'bg-blue-200 text-blue-700'
                        }`}>
                          {submission.read ? 'Read' : 'New'}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {submission.createdAt?.toDate?.() ? 
                            new Date(submission.createdAt.toDate()).toLocaleString() : 
                            'Recent'
                          }
                        </p>
                        {submission.read && submission.readAt && (
                          <p className="text-xs text-green-600 mt-1">
                            Read: {submission.readAt?.toDate?.() ? 
                              new Date(submission.readAt.toDate()).toLocaleString() : 
                              'Recently'
                            }
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border">
                      <p className="text-gray-700 whitespace-pre-wrap">{submission.message}</p>
                    </div>
                    
                    {!submission.read && (
                      <div className="flex justify-end mt-4">
                        <button
                          onClick={() => markAsRead(submission.id)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                        >
                          Mark as Read
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))}
                
                {contactSubmissions.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📩</div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      No contact submissions yet
                    </h3>
                    <p className="text-gray-500">
                      Contact form submissions from users will appear here.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;